import './AthleteQuickSelectTabBar.css';

import React, {
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { AthleteWithId } from '../../../types/athlete';

interface AthleteQuickSelectTabBarProps {
  athletes: AthleteWithId[];
  currentAthleteId: string;
  onAthleteSelect: (athleteId: string) => void;
}

const sortAthletesByNumber = (athletes: AthleteWithId[]): AthleteWithId[] =>
  [...athletes].sort((a, b) => {
    const aNum = Number.parseInt(a.number, 10);
    const bNum = Number.parseInt(b.number, 10);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
    return a.number.localeCompare(b.number);
  });

const AthleteQuickSelectTabBar: React.FC<AthleteQuickSelectTabBarProps> = ({
  athletes,
  currentAthleteId,
  onAthleteSelect,
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const sortedAthletes = useMemo(() => sortAthletesByNumber(athletes), [athletes]);

  useEffect(() => {
    const root = tabsRef.current;
    if (!root) return;
    const activeTab = root.querySelector<HTMLButtonElement>('.quick-select-tab.active');
    activeTab?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentAthleteId, sortedAthletes.length]);

  return (
    <div className="athlete-quick-select-tabs" ref={tabsRef} aria-label="Quick athlete switcher">
      {sortedAthletes.map((athlete) => (
        <button
          key={athlete.id}
          type="button"
          className={[
            'quick-select-tab',
            athlete.id === currentAthleteId ? 'active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => onAthleteSelect(athlete.id)}
        >
          <span className="quick-select-tab-number">#{athlete.number}</span>
          <span className="quick-select-tab-name">{athlete.name}</span>
        </button>
      ))}
    </div>
  );
};

export default AthleteQuickSelectTabBar;
